import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Receipt, RotateCcw, Loader2, Info, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const money = (n, sym = '') => `${sym}${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const day = (iso) => (iso ? new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const REASONS = {
  NOT_SELF_SERVE: 'Included with your enrolment — not refundable here',
  REFUND_PENDING: 'Refund request in review',
  EXPIRED: 'Plan has ended',
  NOTHING_PAID: 'No payment to refund',
  REFUND_LIMIT_REACHED: 'One refund per product per year already used',
  REFUND_TOO_SMALL: 'Remaining amount is below ₹1',
  RATE_UNAVAILABLE: 'Rate unavailable — contact support',
};
const STATUS_TONE = { pending: 'bg-amber-100 text-amber-800', refunded: 'bg-emerald-100 text-emerald-800', rejected: 'bg-rose-100 text-rose-800', cancelled: 'bg-muted text-muted-foreground', closed_dispute: 'bg-muted text-muted-foreground' };

const PLAN_LABEL = { monthly: 'Monthly', till_delivery: 'Till delivery', yearly: 'Yearly' };

function QuoteBreakdown({ q }) {
  const s = q.currency_symbol;
  return (
    <div className="rounded-xl bg-muted/50 p-3 text-xs space-y-1" data-testid="refund-breakdown">
      <div className="flex justify-between"><span className="text-muted-foreground">Paid on {day(q.activated_at)}</span><span>{money(q.paid_amount, s)}</span></div>
      <div className="flex justify-between"><span className="text-muted-foreground">Days used (today counts)</span><span>{q.used_days} of {q.total_days}</span></div>
      <div className="flex justify-between"><span className="text-muted-foreground">Used days at standard monthly rate ({money(q.monthly_rate, s)}/30)</span><span>− {money(q.used_charge, s)}</span></div>
      <div className="flex justify-between font-semibold text-sm border-t pt-1 mt-1"><span>Refund to original payment method</span><span data-testid="refund-quote-amount">{money(q.refund_amount, s)}</span></div>
    </div>
  );
}

export default function PurchasesRefunds() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [reason, setReason] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get('/api/products/refunds'); setData(r.data); }
    catch { toast.error('Could not load your purchases'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post('/api/products/refunds/request', { entitlement_id: active.entitlement_id, reason, accepted_terms: true });
      toast.success('Refund request sent — our team will review it within 3 working days');
      setActive(null); setReason(''); setAccepted(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.detailObject?.code || 'Could not submit request');
    } finally { setSaving(false); }
  };

  const cancel = async (id) => {
    try {
      const r = await api.post(`/api/products/refunds/${id}/cancel`);
      toast.success(`Request cancelled — access restored${r.data.expiry_extended_days ? ` (+${r.data.expiry_extended_days} days added)` : ''}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detailObject?.code || 'Could not cancel'); }
  };

  if (loading) return <Skeleton className="h-40 rounded-2xl" />;
  const purchases = data?.purchases || [];
  const requests = data?.requests || [];
  const pendingByEnt = Object.fromEntries(requests.filter((r) => r.status === 'pending').map((r) => [r.entitlement_id, r]));

  return (
    <div className="space-y-6" data-testid="purchases-refunds">
      <Card className="rounded-2xl">
        <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Receipt className="h-4 w-4" /> Purchases</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {purchases.length === 0 && <p className="text-sm text-muted-foreground" data-testid="purchases-empty">No SoulAI Coach or Baby Tracker purchases yet.</p>}
          {purchases.map((q) => {
            const pending = pendingByEnt[q.entitlement_id];
            return (
              <div key={q.entitlement_id} className="rounded-xl border p-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`purchase-${q.entitlement_id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm">{q.product_label} · {PLAN_LABEL[q.plan] || q.plan}</p>
                  <p className="text-xs text-muted-foreground">
                    {q.source === 'self_serve_payment' ? `Paid ${money(q.paid_amount, q.currency_symbol)} on ${day(q.activated_at)}` : 'Included with your enrolment'}
                    {q.expires_at ? ` · ends ${day(q.expires_at)}` : ''}
                  </p>
                  {!q.eligible && !pending && q.reasons?.[0] && (
                    <p className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1"><Info className="h-3 w-3" />{REASONS[q.reasons[0]] || q.reasons[0]}</p>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <Badge className={STATUS_TONE[pending ? 'pending' : ''] || 'bg-emerald-100 text-emerald-800'} variant="secondary">{pending ? 'Refund in review' : (q.status === 'suspended' ? 'Paused' : q.status)}</Badge>
                  {q.eligible && !pending && (
                    <div className="mt-2">
                      <Button size="sm" variant="outline" className="rounded-full" onClick={() => setActive(q)} data-testid={`refund-request-btn-${q.entitlement_id}`}>
                        <RotateCcw className="h-3.5 w-3.5 mr-1" /> Request refund · {money(q.refund_amount, q.currency_symbol)}
                      </Button>
                    </div>
                  )}
                  {pending && (
                    <div className="mt-2">
                      <Button size="sm" variant="ghost" className="rounded-full text-xs" onClick={() => cancel(pending.id)} data-testid={`refund-cancel-btn-${pending.id}`}>Cancel request & resume</Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          <p className="text-[11px] text-muted-foreground flex items-start gap-1"><ShieldCheck className="h-3.5 w-3.5 shrink-0 mt-0.5" />{data?.policy?.formula} Refunds are reviewed by our team and credited to the original payment method. One refund per product per year. Enrolment fees follow the standard refund policy.</p>
        </CardContent>
      </Card>

      {requests.length > 0 && (
        <Card className="rounded-2xl">
          <CardHeader><CardTitle className="text-base">Refund requests</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {requests.map((r) => (
              <div key={r.id} className="flex items-center justify-between text-sm border-b last:border-0 pb-2 last:pb-0" data-testid={`refund-request-${r.id}`}>
                <div>
                  <p className="font-medium">{r.quote?.product_label || r.product} · {money(r.refund_amount, r.quote?.currency_symbol)}</p>
                  <p className="text-xs text-muted-foreground">Requested {day(r.requested_at)}{r.decided_at ? ` · decided ${day(r.decided_at)}` : ''}{r.reason === 'learner_request_approved_after_cancel' ? ' · Refund approved after cancellation' : ''}{r.note ? ` · ${r.note}` : ''}</p>
                </div>
                <Badge className={STATUS_TONE[r.status] || ''} variant="secondary" data-testid={`refund-status-${r.id}`}>{r.status.replace('_', ' ')}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Dialog open={!!active} onOpenChange={(v) => { if (!v) { setActive(null); setAccepted(false); } }}>
        <DialogContent className="sm:max-w-md" data-testid="refund-dialog">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle>Refund {active.product_label}</DialogTitle>
                <DialogDescription>Here's exactly what you'll get back. The amount is final once you confirm.</DialogDescription>
              </DialogHeader>
              <QuoteBreakdown q={active} />
              <Textarea placeholder="Tell us why (optional)" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} data-testid="refund-reason" />
              <label className="flex items-start gap-2 text-xs cursor-pointer">
                <input type="checkbox" className="mt-0.5" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} data-testid="refund-accept-terms" />
                <span>I understand my access to {active.product_label} pauses now, the refund of <b>{money(active.refund_amount, active.currency_symbol)}</b> is reviewed by the team within 3 working days and credited to my original payment method in 5–7 working days. I can cancel the request while it's in review.</span>
              </label>
              <DialogFooter className="gap-2">
                <Button variant="ghost" onClick={() => setActive(null)} disabled={saving}>Back</Button>
                <Button className="rounded-full" onClick={submit} disabled={!accepted || saving} data-testid="refund-submit">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm refund request'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
